/**
 * Dev-only Chart Creator template probe.
 *
 * FactSet documents the Chart Creator *request* contract but not the layout of
 * the workbook it returns, so every edit path that writes into a downloaded
 * workbook is guessing until someone looks at real bytes. This module is that
 * look: it dumps everything needed to author a fingerprint in
 * `chartCreatorTemplateMap.ts`, and nothing else.
 *
 * It is deliberately NOT registered in `EXECUTOR_RUNTIME_TOOLS` — the agent
 * must never be able to call it. Reach it from the browser console after
 * enabling diagnostics (`localStorage.setItem("finster:diag","1")`, reload):
 *
 *   await window.__finsterChartCreatorTemplateProbe.downloadTemplates()
 *   await window.__finsterChartCreatorTemplateProbe.probeOpenWorkbook()
 *   window.__finsterChartCreatorTemplateProbe.deriveFingerprintDraft(report)
 *
 * `downloadTemplates` returns base64 for offline unzipping — `xl/workbook.xml`
 * and `xl/worksheets/*` are where form controls and their linked cells live,
 * and no intent exposes those.
 */

import { getCharts, getWorkbookNamedObjects } from "../excelHostOps";
import {
  listWorksheetsMetadata,
  readRangeContent,
  type WorksheetMetadata,
} from "../excelService";
import { getActiveExcelContext } from "../excelHostOps";
import { downloadExcelChartViaApi } from "../services/factset/chartCreatorApi";
import { parseSingleCellReference } from "../workbookCellReferences";
import type { ChartingChartInfo } from "../services/factset/spreadsheetIntents";
import type { FactsetChartParameters, WorkbookNamedObject } from "../types";
import type {
  ChartCreatorTemplateFingerprint,
  ChartCreatorTemplateInput,
} from "../chartCreatorTemplateMap";
import { isDiagEnabled } from "../aiDiag";

/**
 * Three shapes worth probing before trusting any fingerprint: one summary-table
 * type, one type with no summary table, and one multi-ticker type. A layout
 * that holds across all three is far more likely to be the real template.
 */
export const CHART_CREATOR_PROBE_SPECS: ReadonlyArray<{
  label: string;
  parameters: FactsetChartParameters;
}> = [
  {
    label: "summary-table type",
    parameters: { chartType: "VAL_PE2", tickers: ["AAPL-US"] },
  },
  {
    label: "non-summary type",
    parameters: { chartType: "P_PRICE", tickers: ["AAPL-US"] },
  },
  {
    label: "multi-ticker type",
    parameters: { chartType: "P_INDEX", tickers: ["AAPL-US", "MSFT-US"] },
  },
];

export interface ProbedSheet {
  name: string;
  index: number;
  visibility?: WorksheetMetadata["visibility"];
  usedRangeAddress?: string;
  startRow?: number;
  startColumn?: number;
  truncated?: boolean;
  values?: (string | number | boolean | null)[][];
  formulas?: (string | null)[][];
  readError?: string;
}

export interface ProbedNamedObject {
  name?: string;
  refersTo?: string;
  address?: string;
  scope?: unknown;
  isVisible?: unknown;
  /** Every field the host returned, so nothing is lost to our projection. */
  raw: WorkbookNamedObject;
}

export interface ChartCreatorWorkbookProbeReport {
  workbookName: string;
  capturedAt: string;
  sheets: ProbedSheet[];
  namedObjects: ProbedNamedObject[];
  namedObjectsError?: string;
  charts: ChartingChartInfo[];
  chartsError?: string;
}

export interface ChartCreatorDownloadProbeEntry {
  label: string;
  chartType: string;
  tickers: string[];
  ok: boolean;
  base64Length?: number;
  approximateBytes?: number;
  workbookBase64?: string;
  error?: string;
}

const NAMED_OBJECT_PROPERTIES = [
  "name",
  "displayName",
  "address",
  "rangeAddress",
  "refersTo",
  "reference",
  "formula",
  "scope",
  "isVisible",
  "visible",
  "comment",
];

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function pick(record: WorkbookNamedObject, key: string): unknown {
  if (Object.prototype.hasOwnProperty.call(record, key)) return record[key];
  const match = Object.keys(record).find(
    (candidate) => candidate.toLowerCase() === key.toLowerCase(),
  );
  return match ? record[match] : undefined;
}

function asOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

/**
 * Dump the host-visible structure of an already-open Chart Creator workbook.
 * Reads formulas as well as values — an input cell that a template drives with
 * a formula is not an input cell, and only the formula grid shows that.
 */
export async function probeOpenChartCreatorWorkbook(
  workbookName?: string,
): Promise<ChartCreatorWorkbookProbeReport> {
  const targetWorkbookName =
    workbookName?.trim() ||
    (await getActiveExcelContext()).workbookName?.trim() ||
    "";
  if (!targetWorkbookName) {
    throw new Error(
      "chartCreatorTemplateProbe: no workbook name available; open the Chart Creator workbook first.",
    );
  }

  const sheetMetadata = await listWorksheetsMetadata(targetWorkbookName);
  const sheets: ProbedSheet[] = [];
  for (const sheet of sheetMetadata) {
    const entry: ProbedSheet = {
      name: sheet.name,
      index: sheet.index,
      visibility: sheet.visibility,
      usedRangeAddress: sheet.usedRange?.address,
    };
    if (sheet.usedRange) {
      try {
        const read = await readRangeContent(
          sheet.name,
          sheet.usedRange.address,
          { includeFormulas: true, includeFormats: false },
          targetWorkbookName,
        );
        entry.truncated = read.truncated;
        entry.startRow = read.range?.startRow;
        entry.startColumn = read.range?.startColumn;
        entry.values = read.range?.values;
        entry.formulas = read.range?.formulas;
      } catch (error) {
        entry.readError = errorText(error);
      }
    }
    sheets.push(entry);
  }

  let namedObjects: ProbedNamedObject[] = [];
  let namedObjectsError: string | undefined;
  try {
    const raw = await getWorkbookNamedObjects({
      workbookName: targetWorkbookName,
      skipHidden: false,
      properties: NAMED_OBJECT_PROPERTIES,
    });
    namedObjects = raw.map((record) => ({
      name: asOptionalString(pick(record, "name")),
      refersTo: asOptionalString(pick(record, "refersTo")),
      address: asOptionalString(pick(record, "address")),
      scope: pick(record, "scope"),
      isVisible: pick(record, "isVisible"),
      raw: record,
    }));
  } catch (error) {
    namedObjectsError = errorText(error);
  }

  let charts: ChartingChartInfo[] = [];
  let chartsError: string | undefined;
  try {
    charts = await getCharts({
      workbookName: targetWorkbookName,
      detail: "Full",
    });
  } catch (error) {
    chartsError = errorText(error);
  }

  return {
    workbookName: targetWorkbookName,
    capturedAt: new Date().toISOString(),
    sheets,
    namedObjects,
    namedObjectsError,
    charts,
    chartsError,
  };
}

/**
 * Fetch raw `.xlsx` bytes for the probe specs without opening anything. Unzip
 * the base64 offline to inspect form controls and their linked cells, which no
 * intent surfaces.
 */
export async function downloadChartCreatorTemplates(
  specs: ReadonlyArray<{
    label: string;
    parameters: FactsetChartParameters;
  }> = CHART_CREATOR_PROBE_SPECS,
  options: { includeBase64?: boolean; signal?: AbortSignal } = {},
): Promise<ChartCreatorDownloadProbeEntry[]> {
  const results: ChartCreatorDownloadProbeEntry[] = [];
  for (const spec of specs) {
    const entry: ChartCreatorDownloadProbeEntry = {
      label: spec.label,
      chartType: spec.parameters.chartType ?? "",
      tickers: spec.parameters.tickers ?? [],
      ok: false,
    };
    try {
      const download = await downloadExcelChartViaApi(spec.parameters, {
        signal: options.signal,
      });
      const base64 = download.workbookBase64 ?? "";
      entry.ok = base64.length > 0;
      entry.base64Length = base64.length;
      entry.approximateBytes = Math.round((base64.length * 3) / 4);
      if (options.includeBase64 !== false) entry.workbookBase64 = base64;
      if (!entry.ok) entry.error = download.error ?? "empty workbook bytes";
    } catch (error) {
      entry.error = errorText(error);
    }
    results.push(entry);
  }
  return results;
}

/**
 * Turn a probe report into a fingerprint skeleton to paste into
 * `chartCreatorTemplateMap.ts`.
 *
 * This intentionally produces a **draft**: it lists the single-cell named
 * objects it found so a human can decide which are genuine parameter inputs.
 * Nothing here is auto-registered, because an auto-derived fingerprint that is
 * wrong would send writes to the wrong cells — the exact failure the
 * match-never-guess rule exists to prevent.
 */
export function deriveFingerprintDraft(
  report: ChartCreatorWorkbookProbeReport,
  version = `chart-creator/${report.capturedAt.slice(0, 7)}`,
): {
  version: string;
  probedChartTypes: string[];
  detectSheetNames: string[];
  candidateInputs: Record<string, ChartCreatorTemplateInput>;
  unresolvedNamedObjects: ProbedNamedObject[];
  suggested: Pick<ChartCreatorTemplateFingerprint, "version" | "probedChartTypes">;
} {
  const candidateInputs: Record<string, ChartCreatorTemplateInput> = {};
  const unresolved: ProbedNamedObject[] = [];
  for (const namedObject of report.namedObjects) {
    // Same parser the edit path uses, so a draft can never claim a cell the
    // real discovery would reject. Scope is left out deliberately: a name the
    // probe cannot place on its own is one a human should look at.
    const parsed =
      parseSingleCellReference(namedObject.refersTo) ??
      parseSingleCellReference(namedObject.address);
    if (!namedObject.name || !parsed) {
      unresolved.push(namedObject);
      continue;
    }
    candidateInputs[namedObject.name] = {
      sheet: parsed.sheetName,
      address: parsed.address,
    };
  }
  return {
    version,
    probedChartTypes: [],
    detectSheetNames: report.sheets.map((sheet) => sheet.name),
    candidateInputs,
    unresolvedNamedObjects: unresolved,
    suggested: { version, probedChartTypes: [] },
  };
}

interface ChartCreatorTemplateProbeApi {
  probeOpenWorkbook: typeof probeOpenChartCreatorWorkbook;
  downloadTemplates: typeof downloadChartCreatorTemplates;
  deriveFingerprintDraft: typeof deriveFingerprintDraft;
  specs: typeof CHART_CREATOR_PROBE_SPECS;
}

/**
 * Attach the probe to `globalThis` for console access. No-op unless
 * diagnostics are enabled, so production builds never expose it.
 */
export function registerChartCreatorTemplateProbe(): boolean {
  if (typeof globalThis === "undefined") return false;
  if (!isDiagEnabled()) return false;
  const api: ChartCreatorTemplateProbeApi = {
    probeOpenWorkbook: probeOpenChartCreatorWorkbook,
    downloadTemplates: downloadChartCreatorTemplates,
    deriveFingerprintDraft,
    specs: CHART_CREATOR_PROBE_SPECS,
  };
  (globalThis as Record<string, unknown>).__finsterChartCreatorTemplateProbe =
    api;
  return true;
}
